import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSubscription, useApolloClient, useReactiveVar } from '@apollo/client';
import { DOCUMENT_STATE_UPDATED, CHANGE_DOCUMENT_STATE_FAILED } from '../../../stepper/subscriptions';
import {
  DOCUMENT_NAVIGATION_STEP_CHANGED,
  GENERATION_STATUS_CHANGED,
  INSURANCE_DOCUMENT_UPDATED
} from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import { QUOTATIONS_STATUS_CHANGED } from 'features/insuranceDocument/edit/root/subscriptions';
import {
  INSURANCE_QUOTATIONS_INVALIDATED,
  RTI_RELATED_OFFER_FOUND,
  CASCO_QUOTATION_DATA_CHANGED,
  CONVENTIONS_INITALIZED
} from '../../offer/casco/subscriptions';
import { ASSET_SAVED } from 'features/insuranceDocument/edit/general/asset/subscriptions';
import { QUOTATIONS_INITIALIZED } from 'features/insuranceDocument/edit/offer/comparator/subscriptions';
import { RCA_QUOTATION_DATA_CHANGED, QUOTATION_COMPUTED, INSURANCE_OFFERS_INVALIDATED } from '../../offer/comparator/subscriptions';
import { POLICY_DATA_SAVED } from '../subscriptions';
import { POLICY_FILES_UPLOADED } from 'features/insuranceDocument/edit/policy/policyData/subscriptions';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks';
import { useConditionalSubscription } from 'features/common/security/hooks';
import { invalidateCascoQuotations } from '../../offer/casco/quotationsUtils';
import { quotationsInitializedVar, generationInProgressStatusVar, quotationsStatusVar } from 'features/insuranceDocument/variables';
import { invalidateInsuranceOffers } from '../../offer/comparator/insuranceOffersUtils';

const getDocumentId = () => {
  const route = window.location.href;
  if (route.includes('insurance-documents')) {
    const result = route.match(/(?:insurance-documents\/)([0-9]+)/);
    if (!result) return null;
    const id = result[1];
    if (!id) return null;
    const documentId = Number(id);
    return documentId;
  }
  return null;
};

const OverRouterSubscriptionAreaForSavedDocument = ({ contextRef }) => {
  const [documentStateData, setDocumentStateData] = useState(null);
  const [navigationStepData, setNavigationStepData] = useState(null);
  const quotationsStatus = useReactiveVar(quotationsStatusVar);
  const generationInProgress = useReactiveVar(generationInProgressStatusVar);
  const [errorData, setErrorData] = useState(null);
  const [quotationsStatusChanged, setQuotationsStatusChanged] = useState();
  const [generationStatusChanged, setGenerationStatusChanged] = useState();
  const documentId = getDocumentId();
  const addToast = useToast();
  const client = useApolloClient();
  const { t } = useTranslation();

  const cleanDocumentStateData = () => {
    setDocumentStateData(null);
  };

  const cleanErrorData = () => {
    setErrorData(null);
  };

  const cleanNavigationStepData = () => {
    setNavigationStepData(null);
  };

  useEffect(() => {
    if (!documentStateData) return;
    const { onDocumentStateUpdated } = contextRef.current.events;
    onDocumentStateUpdated(documentStateData);
    cleanDocumentStateData();
  }, [contextRef.current.events.onDocumentStateUpdated, documentStateData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!errorData) return;
    contextRef.current.events.onChangeDocumentStateFailed.forEach(z => z(errorData));
    cleanErrorData();
  }, [contextRef.current.events.onChangeDocumentStateFailed, errorData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!navigationStepData) return;
    const { onNavigationStepChanged } = contextRef.current.events;
    onNavigationStepChanged(navigationStepData);
    cleanNavigationStepData();
  }, [contextRef.current.events.onNavigationStepChanged, navigationStepData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!quotationsStatusChanged) return;
    const { onQuotationsStatusChanged } = contextRef.current.events;
    onQuotationsStatusChanged(quotationsStatus);
    setQuotationsStatusChanged(false);
  }, [contextRef.current.events.onQuotationsStatusChanged, quotationsStatusChanged, quotationsStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!generationStatusChanged) return;
    const { onGenerationStatusChanged } = contextRef.current.events;
    onGenerationStatusChanged(generationInProgress);
    setGenerationStatusChanged(false);
  }, [contextRef.current.events.onGenerationStatusChanged, generationInProgress, generationStatusChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useSubscription(DOCUMENT_STATE_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setDocumentStateData(data?.documentStateUpdated);
      if (data?.documentStateUpdated.silentMode === true) return;
      if (!data?.documentStateUpdated.messageCode)
        addToast(t('General.StatusChanged'), 'success', { toastId: 'documentStateUpdatedToast' });
      else addToast(t(`StateTransitionCustomMessage.${data?.documentStateUpdated.messageCode}`), 'success');
    }
  });

  useSubscription(CHANGE_DOCUMENT_STATE_FAILED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setErrorData(data);
      addToast(t('General.ChangeDocumentStateFailed', { message: data.changeDocumentStateFailed.message }), 'error', {
        toastId: 'documentStateFailedToast'
      });
    }
  });

  useSubscription(DOCUMENT_NAVIGATION_STEP_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setNavigationStepData(data?.documentNavigationStepChanged);
    }
  });

  useSubscription(QUOTATIONS_STATUS_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      quotationsStatusVar(data?.quotationsStatusChanged?.quotationsStatus);
      setQuotationsStatusChanged(true);
    }
  });

  useSubscription(CONVENTIONS_INITALIZED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {
      quotationsInitializedVar(true);
    }
  });

  useSubscription(INSURANCE_QUOTATIONS_INVALIDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {
      invalidateCascoQuotations(client, documentId);
    }
  });

  useSubscription(CASCO_QUOTATION_DATA_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {
      invalidateCascoQuotations(client, documentId);
    }
  });

  useSubscription(INSURANCE_OFFERS_INVALIDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {
      invalidateInsuranceOffers(client, documentId);
    }
  });

  useSubscription(RCA_QUOTATION_DATA_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {}
  });

  useSubscription(RTI_RELATED_OFFER_FOUND, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      addToast(t('Offer.Edit.General.RTIPolicyGenerationWarning'), 'warning', { toastId: 'rtiRelatedOfferFoundToast' });
    }
  });

  useSubscription(GENERATION_STATUS_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      generationInProgressStatusVar(data?.generationStatusChanged?.generationInProgress);
      setGenerationStatusChanged(true);
    }
  });

  /* Subscription used only for automatic apollo cache updating */

  useSubscription(INSURANCE_DOCUMENT_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {}
  });

  useSubscription(QUOTATIONS_INITIALIZED, {
    variables: { documentId },
    skip: !documentId,
    onData: () => {}
  });

  useSubscription(POLICY_DATA_SAVED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {}
  });

  useConditionalSubscription(POLICY_FILES_UPLOADED, {
    onData: _ => {}
  });

  useSubscription(QUOTATION_COMPUTED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {}
  });

  useSubscription(ASSET_SAVED, {
    variables: { documentId },
    onData: _ => {}
  });

  return '';
};

const OverRouterSubscriptionArea = ({ contextRef }) => {
  const documentId = getDocumentId();
  if (documentId === null) return '';

  return <OverRouterSubscriptionAreaForSavedDocument contextRef={contextRef} />;
};

OverRouterSubscriptionArea.propTypes = {
  contextRef: PropTypes.shape({
    current: PropTypes.shape({
      properties: PropTypes.object.isRequired,
      events: PropTypes.shape({
        onChangeDocumentStateFailed: PropTypes.array.isRequired,
        onDocumentStateUpdated: PropTypes.func.isRequired,
        onNavigationStepChanged: PropTypes.func.isRequired
      }),
      functions: PropTypes.object.isRequired
    })
  })
};

export default OverRouterSubscriptionArea;
